import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Image as ImageIcon, Layers, ArrowLeft, Upload, Wand2, Check, Volume2, Loader2, Brain } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, getToken } from "../lib/api";
import { CharacterPicker } from "../components/CharacterPicker";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import { PhotoCropper, checkPhotoFile, loadPhotoDownscaled } from "../components/PhotoCropper";
import { PortraitEngineCards, PortraitPresetChips, ReferencePhotoRules, usePortraitEngines } from "../components/PortraitEngine";

const METHODS = [
  { id: "describe", icon: Sparkles, title: "Describe with AI", desc: "Tulis karakter yang Anda inginkan, AI menyusun profilnya." },
  { id: "photo", icon: ImageIcon, title: "Upload a Photo", desc: "Unggah foto sebagai referensi penampilan." },
  { id: "combine", icon: Layers, title: "Combine Both", desc: "Foto + instruksi teks untuk kontrol penuh." },
];

const VOICE_LABELS = {
  alloy: "Netral & seimbang", nova: "Hangat & ramah", shimmer: "Lembut & cerah",
  echo: "Tenang & jernih", fable: "Ekspresif & bercerita", onyx: "Dalam & berwibawa",
  coral: "Ceria & bersahabat", sage: "Bijak & menenangkan", ash: "Mantap & percaya diri",
};
const VOICE_SAMPLE = "Halo, senang berkenalan dengan Anda. Saya siap membantu kapan saja.";

export default function CreatePersona() {
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const { t } = useI18n();
  const [step, setStep] = useState("method");
  const [method, setMethod] = useState("describe");
  const [engine, setEngine] = useState("standard");
  const [preset, setPreset] = useState(null);
  const engines = usePortraitEngines();
  const enginePrice = engines?.find((e) => e.id === engine)?.credits;
  const [firstRequest] = useState(() => sessionStorage.getItem("aivora_prefill") || "");  // handed over by the Support Agent: becomes the first message to the new assistant
  const [desc, setDesc] = useState("");
  const [characterId, setCharacterId] = useState("default");
  const [photo, setPhoto] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [consent, setConsent] = useState(false);
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [voices, setVoices] = useState([]);
  const [voiceMeta, setVoiceMeta] = useState({ info: {}, realtime: [] });
  const [voice, setVoice] = useState("nova");
  const [voiceModel, setVoiceModel] = useState("gpt-live-1");
  const [previewing, setPreviewing] = useState(null);
  const previewAudioRef = useRef(null);

  useEffect(() => { api.get("/voice/voices").then((r) => { setVoices(r.data.voices || []); setVoiceMeta({ info: r.data.info || {}, realtime: r.data.realtime || [] }); }).catch(() => {}); }, []);
  useEffect(() => () => { try { previewAudioRef.current?.pause(); } catch (e) {} }, []);

  const previewVoice = async (v) => {
    try { previewAudioRef.current?.pause(); } catch (e) {}
    setPreviewing(v);
    try {
      const res = await fetch(`${API_BASE}/voice/tts`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }, body: JSON.stringify({ text: VOICE_SAMPLE, voice: v }) });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = new Audio(URL.createObjectURL(blob));
      previewAudioRef.current = a;
      a.onended = () => setPreviewing(null);
      a.onerror = () => setPreviewing(null);
      await a.play();
      refreshUser();
    } catch (e) { setPreviewing(null); toast.error("Gagal memutar contoh suara"); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const err = checkPhotoFile(f);
    if (err) { toast.error(err); return; }
    try { setCropSrc(await loadPhotoDownscaled(f)); }  // downscaled in the browser first, then crop/zoom so the face is centred
    catch { toast.error("Foto tidak bisa dibaca. Coba JPG atau PNG."); }
  };

  const needPhoto = method !== "describe";

  const generate = async () => {
    if (!desc.trim() && method !== "photo") { toast.error("Jelaskan karakter Anda"); return; }
    if (needPhoto && !photo) { toast.error("Unggah foto terlebih dahulu"); return; }
    if (needPhoto && !consent) { toast.error("Konfirmasi hak penggunaan foto"); return; }
    setBusy(true);
    try {
      const body = { description: desc || "Buat persona berdasarkan foto referensi.", method, character_id: characterId };
      if (photo) body.photo_b64 = photo.split(",")[1];
      const r = await api.post("/personas/generate-profile", body);
      setProfile(r.data.profile);
      refreshUser();
      setStep("review");
      toast.success(`Profil dibuat (${r.data.credits_used} kredit)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat profil"); } finally { setBusy(false); }
  };

  const setPath = (section, key, val) => setProfile((p) => ({ ...p, [section]: { ...(p[section] || {}), [key]: val } }));

  const photoAsPortrait = method === "photo" && !!photo;  // "Upload a Photo": the photo IS the portrait (moderated) — no AI generation

  const saveAndPortrait = async () => {
    setBusy(true);
    try {
      const r = await api.post("/personas", { profile, reference_photo: photo || null, voice, voice_model: voiceModel, character_id: characterId, portrait_from_photo: photoAsPortrait });
      const pid = r.data.id;
      if (photoAsPortrait) toast.success("Persona disimpan — foto Anda dipakai sebagai potret");
      else {
        toast.success("Persona disimpan, potret sedang dibuat di latar belakang…");
        try { await api.post(`/personas/${pid}/portrait`, { style: profile?.appearance?.visual_style || "cinematic realistic", use_reference: !!photo, engine, preset: engine === "cinematic" ? preset : null }); }
        catch (e) { toast.message(e?.response?.data?.detail || "Potret belum bisa dibuat, Anda bisa coba lagi di editor."); }
      }
      if (firstRequest) {  // Support hand-over: open the new assistant's chat and send the saved request right away
        sessionStorage.removeItem("aivora_prefill");
        try { const c = await api.post("/conversations/direct", { persona_id: pid }); sessionStorage.setItem("aivora_autosend", JSON.stringify({ cid: c.data.id, text: firstRequest })); nav(`/chat/${c.data.id}`); return; }
        catch { /* fall through to the persona page */ }
      }
      nav(`/personas/${pid}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-3xl p-5 sm:p-8 lg:p-10 fade-up" data-testid="create-persona-page">
      {cropSrc && <PhotoCropper src={cropSrc} onDone={(d) => { setPhoto(d); setCropSrc(null); }} onCancel={() => setCropSrc(null)} title="Atur foto referensi" />}
      <button onClick={() => (step === "method" ? nav("/personas") : setStep("method"))} className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft size={16} /> {t("common.back")}
      </button>
      <h1 className="text-3xl font-extrabold text-slate-900">{t("persona.create")}</h1>

      {step === "method" && (
        <>
          <p className="mt-1 text-sm text-slate-500">Pilih cara membuat persona Anda.</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {METHODS.map((m) => (
              <button key={m.id} data-testid={`method-${m.id}`} onClick={() => { setMethod(m.id); setStep("input"); }}
                className="aivora-card aivora-card-hover flex flex-col items-start gap-3 p-5 text-left">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: "rgba(0,209,255,.14)", color: "#00D1FF" }}><m.icon size={22} /></span>
                <span className="font-bold text-slate-900">{m.title}</span>
                <span className="text-xs text-slate-500">{m.desc}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {step === "input" && (
        <div className="mt-6 space-y-5">
          {needPhoto && (
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">Foto referensi</label>
              <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 py-8 hover:border-[#00D1FF]" data-testid="photo-upload">
                {photo ? <img src={photo} alt="" className="h-28 w-28 rounded-xl object-cover" /> : <><Upload className="mb-2 text-slate-500" /><span className="text-sm text-slate-500">Klik untuk unggah</span></>}
                <input type="file" accept="image/*" className="hidden" onChange={onFile} />
              </label>
              <button onClick={() => setConsent(!consent)} data-testid="photo-consent" className="mt-3 flex items-center gap-2 text-left text-xs text-slate-600">
                <span className={`flex h-4 w-4 items-center justify-center rounded border ${consent ? "btn-grad border-transparent" : "border-slate-300"}`}>{consent && <Check size={11} />}</span>
                Saya memiliki hak untuk menggunakan foto ini. Foto hanya dipakai sebagai referensi visual.
              </button>
            </div>
          )}
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">
              {method === "photo" ? "Catatan tambahan (opsional)" : "Deskripsikan Penampilan Persona"}
            </label>
            <textarea className="input-dark min-h-[140px]" data-testid="persona-desc"
              placeholder={method === "photo" ? "Contoh: nama Nadia, usia 30-an, gaya visual sinematik." : "Contoh: Perempuan 30-an bernama Nadia, rambut hitam sebahu, kacamata bulat, blazer krem, senyum hangat, gaya visual sinematik realistis."}
              value={desc} onChange={(e) => setDesc(e.target.value)} />
            {method !== "photo" && <p className="mt-1 text-xs text-slate-400">Fokus pada tampilan fisik, pakaian, dan gaya visual — kepribadian diambil dari Karakter Persona di bawah.</p>}
          </div>
          <CharacterPicker value={characterId} onChange={setCharacterId} />
          <button onClick={generate} disabled={busy} data-testid="generate-profile-btn" className="btn-grad flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm">
            <Wand2 size={18} /> {busy ? "Membuat profil..." : "Buat Profil dengan AI"}
          </button>
        </div>
      )}

      {step === "review" && profile && (
        <div className="mt-6 space-y-5" data-testid="profile-review">
          <p className="text-sm text-slate-500">Tinjau & edit profil sebelum menyimpan.</p>
          <Field label="Nama" value={profile.identity?.name || ""} onChange={(v) => setPath("identity", "name", v)} testid="rev-name" />
          <FieldArea label="Ringkasan" value={profile.identity?.summary || ""} onChange={(v) => setPath("identity", "summary", v)} testid="rev-summary" />
          <FieldArea label="Gaya komunikasi" value={profile.personality?.communication_style || ""} onChange={(v) => setPath("personality", "communication_style", v)} testid="rev-style" />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Rambut" value={profile.appearance?.hair || ""} onChange={(v) => setPath("appearance", "hair", v)} testid="rev-hair" />
            <Field label="Pakaian" value={profile.appearance?.clothing || ""} onChange={(v) => setPath("appearance", "clothing", v)} testid="rev-clothing" />
            <Field label="Gaya visual" value={profile.appearance?.visual_style || ""} onChange={(v) => setPath("appearance", "visual_style", v)} testid="rev-visual" />
            <Field label="Bahasa utama" value={profile.language?.primary || ""} onChange={(v) => setPath("language", "primary", v)} testid="rev-lang" />
          </div>
          <FieldArea label="Instruksi sistem" value={profile.system_instructions || ""} onChange={(v) => setProfile((p) => ({ ...p, system_instructions: v }))} testid="rev-sysinstr" />
          <div className="flex items-center gap-3 rounded-xl border border-[#D9E2F2] bg-[#F5F8FF] px-4 py-3" data-testid="persona-brain-note">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#2F6BFF]/10 text-[#2F6BFF]"><Brain size={18} /></span>
            <span className="min-w-0 text-sm"><span className="block font-semibold text-slate-900">Otak asisten: GPT Luna</span><span className="block text-xs text-slate-500">Model tiap Agen diatur di tab Pengaturan Agen setelah asisten dibuat (bawaan mengikuti rekomendasi platform / Settings). Tool penyedia (pencarian web, eksekusi kode, Google Search/Maps) aktif otomatis.</span></span>
          </div>
          <div>
            <label className="mb-1 mt-5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Karakter Suara</label>
            <p className="mb-2 text-xs text-slate-400">Warna suara asisten di panggilan. Tekan ikon speaker untuk mendengar contoh.</p>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {voices.map((v) => {
                const on = voice === v;
                return (
                  <div key={v} data-testid={`voice-${v}`}
                    className={`flex items-center gap-2 rounded-xl border p-3 transition ${on ? "border-[#2F6BFF] bg-[#EEF3FF]" : "border-[#E7ECF3] hover:bg-slate-50"}`}>
                    <button type="button" onClick={() => setVoice(v)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                      <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${on ? "btn-grad border-transparent" : "border-slate-300"}`}>{on && <Check size={10} />}</span>
                      <span className="min-w-0">
                        <span className="flex items-center gap-1.5 truncate text-sm font-bold capitalize text-slate-900">{v}{voiceMeta.realtime.includes(v) && <span className="rounded bg-[#2F6BFF]/10 px-1 text-[9px] font-bold uppercase tracking-wider text-[#2F6BFF]" data-testid={`voice-rt-${v}`}>Realtime</span>}</span>
                        <span className="block truncate text-xs text-slate-400">{voiceMeta.info[v] || VOICE_LABELS[v] || "Suara"}</span>
                      </span>
                    </button>
                    <button type="button" onClick={() => previewVoice(v)} data-testid={`voice-preview-${v}`}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#E7ECF3] text-[#2F6BFF] hover:bg-[#EEF3FF]" title="Dengar contoh">
                      {previewing === v ? <Loader2 size={15} className="animate-spin" /> : <Volume2 size={15} />}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          {photoAsPortrait && (
            <div className="flex items-center gap-3 rounded-xl border border-[#D9E2F2] bg-[#F5F8FF] px-4 py-3" data-testid="photo-portrait-note">
              <img src={photo} alt="" className="h-12 w-12 shrink-0 rounded-xl object-cover" />
              <span className="text-xs text-slate-600">Foto yang Anda unggah dipakai langsung sebagai potret (tanpa pembuatan AI, tanpa kredit). Foto diperiksa otomatis — konten pornografi/eksplisit ditolak. Anda bisa mengganti foto atau membuat potret AI nanti di halaman persona.</span>
            </div>
          )}
          {!photoAsPortrait && (
            <div className="space-y-2" data-testid="create-portrait-engine">
              <PortraitEngineCards engines={engines} value={engine} onChange={setEngine} disabled={busy} />
              {engine === "cinematic" && <PortraitPresetChips engines={engines} engine={engine} value={preset} onChange={setPreset} disabled={busy} />}
              {photo && <ReferencePhotoRules compact />}
              {enginePrice != null && (
                <p className="text-[11px] text-slate-500" data-testid="create-portrait-price">
                  Potret: <b>{enginePrice} kredit</b>. {engine === "cinematic" ? "Ditahan saat mulai, dilunasi saat tersimpan, dikembalikan penuh bila gagal atau dihentikan." : "Dipotong hanya bila potret berhasil dibuat."}
                </p>
              )}
            </div>
          )}
          {firstRequest && (
            <div className="rounded-xl border border-[#D9E2F2] bg-[#F5F8FF] px-4 py-3 text-xs text-slate-600" data-testid="first-request-note">
              Permintaan pertama tersimpan: <i>“{firstRequest.slice(0, 160)}{firstRequest.length > 160 ? "…" : ""}”</i> — langsung dikirim ke asisten ini setelah disimpan.
            </div>
          )}
          <button onClick={saveAndPortrait} disabled={busy} data-testid="save-persona-btn" className="btn-grad flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm">
            <Sparkles size={18} /> {busy ? (photoAsPortrait ? "Menyimpan & memeriksa foto..." : "Menyimpan & membuat potret...") : photoAsPortrait ? "Simpan Persona" : `Simpan & Buat Potret${enginePrice != null ? ` · ${enginePrice} kredit` : ""}`}
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
      <input className="input-dark" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}
function FieldArea({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
      <textarea className="input-dark min-h-[80px]" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

